import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as dmsService from "../../../services/dmsService";
import useModuleAccess from "../../../hooks/useModuleAccess";
import { hasModulePermission } from "../../../constants/platformModules";
import useFinOptions from "../../Fin/useFinOptions";
import useDmsOptions from "../useDmsOptions";
import { fmtMoney } from "../../Fin/gl/glContext";
import { DataTable, hint } from "../../Fin/gl/glUi";
import { ActionHistory, ChecklistForm, DmsBadge, DocHead, Panel, Section, run } from "./dmsUi";
import { EmptyState, FormDrawer, LoadState, PageHeader, PrimaryButton, SecondaryButton, formatDate, formatDateTime, pageStyle, todayYmd, useLoad } from "../../../components/ui";

// Test drives, trade-ins and return orders.

function useLookup(type) {
  const { data } = useLoad(() => dmsService.listLookups({ lookup_type: type, status: "ACTIVE" }), [type]);
  return (data || []).map((r) => ({ label: r.name, value: r.code }));
}
const isManager = (access) => hasModulePermission(access, "DMS", "DMS_SALES_MANAGER", "DMS_ADMIN");

/* ------------------------------ test drives ------------------------------ */

function TestDriveView({ id, onHide, onChanged }) {
  const { data: t, loading, error, reload } = useLoad(() => dmsService.getTestDrive(id), [id]);
  const [form, setForm] = useState(null);
  const refresh = () => {
    reload();
    onChanged();
  };
  return (
    <Panel title={t ? `Test drive ${t.td_number}` : "Test drive"} onHide={onHide} width={820}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {t ? (
          <>
            <DocHead title="" number={t.customer_name} status={t.status} sub={`${t.vehicle.make_name} ${t.vehicle.model_name} ${t.vehicle.variant_name} · VIN ${t.vehicle.vin} · ${formatDateTime(t.scheduled_at)} · ${t.salesperson_name || ""}`}>
              {t.status === "SCHEDULED" ? <PrimaryButton onClick={() => setForm("start")}>Start</PrimaryButton> : null}
              {t.status === "OUT" ? <PrimaryButton onClick={() => setForm("finish")}>Vehicle returned</PrimaryButton> : null}
              {t.status === "SCHEDULED" ? <SecondaryButton onClick={() => window.confirm("Cancel this test drive?") && run(() => dmsService.testDriveAction(id, "cancel"), refresh)}>Cancel</SecondaryButton> : null}
            </DocHead>
            <Section title="Driver and drive">
              <div style={{ fontSize: 14 }}>
                Licence {t.licence_no} (valid to {formatDate(t.licence_expiry)}) · phone {t.customer_phone || "—"}
                <br />
                Odometer out {t.odometer_out ?? "—"} · in {t.odometer_in ?? "—"} {t.feedback_rating ? `· rated ${t.feedback_rating}/5` : ""}
                {t.feedback ? <div style={hint}>{t.feedback}</div> : null}
              </div>
            </Section>
            <Section title="Checklist">
              <ChecklistForm checklist={t.checklist} disabled={["RETURNED", "CANCELLED"].includes(t.status)} onSave={(body) => run(() => dmsService.testDriveAction(id, "checklist", body), reload)} />
            </Section>
            <Section title="Action history">
              <ActionHistory actions={t.actions} />
            </Section>
          </>
        ) : null}
      </LoadState>
      <FormDrawer show={form === "start"} title="Start the test drive" initial={{}} onHide={() => setForm(null)} onDone={refresh} onSave={(clean) => dmsService.testDriveAction(id, "start", clean)} fields={[{ key: "odometer_out", label: "Odometer out", type: "number", min: 0 }]} />
      <FormDrawer
        show={form === "finish"}
        title="Vehicle returned"
        initial={{}}
        onHide={() => setForm(null)}
        onDone={refresh}
        onSave={(clean) => dmsService.testDriveAction(id, "finish", { ...clean, odometer_in: Number(clean.odometer_in), feedback_rating: clean.feedback_rating ? Number(clean.feedback_rating) : undefined })}
        fields={[
          { key: "odometer_in", label: "Odometer in", type: "number", min: 0, required: true },
          { key: "feedback_rating", label: "Customer rating (1 to 5)", type: "select", options: [1, 2, 3, 4, 5].map((n) => ({ label: String(n), value: n })) },
          { key: "feedback", label: "Feedback", type: "textarea", max: 1000 },
        ]}
      />
    </Panel>
  );
}

export function TestDrives() {
  const [params] = useSearchParams();
  const fin = useFinOptions(["locations"]);
  const prospects = useLoad(() => dmsService.listProspects({ status: "OPEN", size: 200 }), []);
  const units = useLoad(() => dmsService.listVehicles({ status: "IN_STOCK", size: 500 }), []);
  const { data, loading, error, reload } = useLoad(() => dmsService.listTestDrives({}), []);
  const [open, setOpen] = useState(null);
  const presetProspect = params.get("prospect");
  const [creating, setCreating] = useState(!!presetProspect);
  const unitOptions = (units.data || [])
    .slice()
    .sort((a, b) => (b.stock_type === "DEMO") - (a.stock_type === "DEMO"))
    .map((u) => ({ label: `${u.stock_type === "DEMO" ? "Demo · " : ""}${u.model_name} ${u.variant_name} · ${u.vin}`, value: u.id }));
  return (
    <div style={pageStyle}>
      <PageHeader title="Test Drives" subtitle="Book demo drives, record odometer and feedback" actions={<PrimaryButton onClick={() => setCreating(true)}>Book test drive</PrimaryButton>} />
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => setOpen(r.id)}
          empty={<EmptyState title="No test drives" />}
          columns={[
            { label: "Number", render: (r) => <b>{r.td_number}</b> },
            { label: "When", render: (r) => formatDateTime(r.scheduled_at) },
            { label: "Customer", render: (r) => r.customer_name },
            { label: "Vehicle", render: (r) => `${r.model_name} ${r.variant_name}` },
            { label: "Consultant", render: (r) => r.salesperson_name },
            { label: "Rating", num: true, render: (r) => (r.feedback_rating ? `${r.feedback_rating}/5` : "—") },
            { label: "Status", render: (r) => <DmsBadge status={r.status} /> },
          ]}
        />
      </LoadState>
      {open ? <TestDriveView id={open} onHide={() => setOpen(null)} onChanged={reload} /> : null}
      <FormDrawer
        show={creating}
        title="Book a test drive"
        initial={{ scheduled_at: `${todayYmd()}T10:00`, prospect_id: presetProspect || undefined, lead_id: params.get("lead") || undefined }}
        onHide={() => setCreating(false)}
        onDone={(d) => {
          reload();
          if (d && d.id) setOpen(d.id);
        }}
        width={600}
        onSave={(clean) => dmsService.createTestDrive(clean)}
        fields={(v) => [
          { key: "unit_id", label: "Vehicle", type: "select", required: true, options: unitOptions },
          { key: "location_id", label: "Location (blank: where the vehicle is)", type: "select", options: fin.locations || [] },
          { key: "prospect_id", label: "Prospect", type: "select", options: (prospects.data || []).map((p) => ({ label: `${p.display_name} ${p.phone}`, value: p.id })) },
          ...(v.prospect_id ? [] : [{ key: "customer_name", label: "Customer name", required: true, max: 200 }, { key: "customer_phone", label: "Phone", max: 30 }]),
          { key: "licence_no", label: "Driving licence number", required: true, max: 40 },
          { key: "licence_expiry", label: "Licence valid to", type: "date", required: true },
          { key: "scheduled_at", label: "Date and time", type: "datetime-local", required: true },
        ]}
      />
    </div>
  );
}

/* ------------------------------ trade-ins ------------------------------ */

function TradeInView({ id, onHide, onChanged }) {
  const { access } = useModuleAccess();
  const { data: t, loading, error, reload } = useLoad(() => dmsService.getTradeIn(id), [id]);
  const orders = useLoad(() => dmsService.listOrders({ open: 1 }), []);
  const cat = useDmsOptions(["variants"]);
  const [form, setForm] = useState(null);
  const refresh = () => {
    reload();
    onChanged();
  };
  return (
    <Panel title={t ? `Trade-in ${t.ti_number}` : "Trade-in"} onHide={onHide} width={820}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {t ? (
          <>
            <DocHead title="" number={`${t.make_name} ${t.model_name} ${t.model_year || ""}`} status={t.status} sub={`${t.customer_name} · VIN ${t.vin}${t.so_number ? ` · order ${t.so_number}` : ""}${t.quote_number ? ` · quotation ${t.quote_number}` : ""}`}>
              {t.status === "APPRAISED" ? <PrimaryButton onClick={() => run(() => dmsService.tradeInAction(id, "submit"), refresh)}>Submit value</PrimaryButton> : null}
              {["APPRAISED", "APPROVED"].includes(t.status) ? <SecondaryButton onClick={() => setForm("link")}>Link to order</SecondaryButton> : null}
              {t.status === "APPROVED" && isManager(access) ? <PrimaryButton onClick={() => setForm("receive")}>Receive into stock</PrimaryButton> : null}
              {["APPRAISED", "APPROVED", "REJECTED"].includes(t.status) ? <SecondaryButton onClick={() => window.confirm("Cancel this trade-in?") && run(() => dmsService.tradeInAction(id, "cancel"), refresh)}>Cancel</SecondaryButton> : null}
            </DocHead>
            <Section title="Valuation">
              <div className="row" style={{ fontSize: 14 }}>
                <div className="col-md-6">
                  <div>Appraised value: <b>{fmtMoney(t.appraised_value)}</b></div>
                  <div>Allowance offered: <b>{fmtMoney(t.offered_allowance)}</b></div>
                  {t.over_allowance ? <div style={{ color: "#92400E" }}>Over-allowance: {fmtMoney(t.over_allowance)} (needs approval)</div> : null}
                </div>
                <div className="col-md-6">
                  <div>Odometer: {t.odometer ?? "—"} · grade {t.condition_grade || "—"}</div>
                  <div>Lien: {t.lien_holder || "none"} {Number(t.lien_payoff) ? fmtMoney(t.lien_payoff) : ""}</div>
                  <div>Appraiser: {t.appraiser_name || "—"}</div>
                </div>
              </div>
            </Section>
            <Section title="Appraisal checklist">
              <ChecklistForm checklist={t.checklist} disabled={["RECEIVED", "CANCELLED"].includes(t.status)} onSave={(body) => run(() => dmsService.tradeInAction(id, "checklist", body), reload)} />
            </Section>
            <Section title="Action history">
              <ActionHistory actions={t.actions} />
            </Section>
          </>
        ) : null}
      </LoadState>
      <FormDrawer
        show={form === "link"}
        title="Link to sales order"
        initial={{}}
        onHide={() => setForm(null)}
        onDone={refresh}
        onSave={(clean) => dmsService.tradeInAction(id, "link", clean)}
        fields={[{ key: "so_id", label: "Sales order", type: "select", required: true, options: (orders.data || []).filter((o) => ["ENTERED", "PENDING_APPROVAL", "BOOKED"].includes(o.status)).map((o) => ({ label: `${o.so_number} · ${o.customer_name}`, value: o.id })) }]}
      />
      <FormDrawer
        show={form === "receive"}
        title="Receive into used stock"
        initial={{ received_date: todayYmd() }}
        onHide={() => setForm(null)}
        onDone={refresh}
        onSave={(clean) => dmsService.tradeInAction(id, "receive", clean)}
        fields={[
          { key: "variant_id", label: "Sold as variant (catalogue)", type: "select", required: true, options: cat.variants || [] },
          { key: "received_date", label: "Received on", type: "date", required: true },
        ]}
      >
        <div style={hint}>The vehicle enters used stock at its appraised value (or the allowance if lower); the customer's allowance is applied on the order's invoice.</div>
      </FormDrawer>
    </Panel>
  );
}

export function TradeIns() {
  const fin = useFinOptions(["locations"]);
  const orders = useLoad(() => dmsService.listOrders({ open: 1 }), []);
  const quotes = useLoad(() => dmsService.listQuotes({ open: 1 }), []);
  const { data, loading, error, reload } = useLoad(() => dmsService.listTradeIns({}), []);
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);
  return (
    <div style={pageStyle}>
      <PageHeader title="Trade-ins" subtitle="Appraise part-exchange vehicles; the allowance is credited on the order's invoice" actions={<PrimaryButton onClick={() => setCreating(true)}>New appraisal</PrimaryButton>} />
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => setOpen(r.id)}
          empty={<EmptyState title="No trade-ins" />}
          columns={[
            { label: "Number", render: (r) => <b>{r.ti_number}</b> },
            { label: "Customer", render: (r) => r.customer_name },
            { label: "Vehicle", render: (r) => `${r.make_name} ${r.model_name} ${r.model_year || ""}` },
            { label: "VIN", render: (r) => r.vin },
            { label: "Value", num: true, render: (r) => fmtMoney(r.appraised_value) },
            { label: "Allowance", num: true, render: (r) => fmtMoney(r.offered_allowance) },
            { label: "Status", render: (r) => <DmsBadge status={r.status} /> },
          ]}
        />
      </LoadState>
      {open ? <TradeInView id={open} onHide={() => setOpen(null)} onChanged={reload} /> : null}
      <FormDrawer
        show={creating}
        title="Appraise a trade-in"
        initial={{ condition_grade: "B" }}
        onHide={() => setCreating(false)}
        onDone={(d) => {
          reload();
          if (d && d.id) setOpen(d.id);
        }}
        width={640}
        onSave={(clean) => dmsService.saveTradeIn(null, { ...clean, appraised_value: Number(clean.appraised_value), offered_allowance: Number(clean.offered_allowance), lien_payoff: Number(clean.lien_payoff || 0) })}
        fields={(v) => [
          { key: "so_id", label: "Sales order", type: "select", options: (orders.data || []).filter((o) => ["ENTERED", "PENDING_APPROVAL", "BOOKED"].includes(o.status)).map((o) => ({ label: `${o.so_number} · ${o.customer_name}`, value: o.id })) },
          ...(v.so_id ? [] : [{ key: "quote_id", label: "or quotation", type: "select", options: (quotes.data || []).map((q) => ({ label: `${q.quote_number} · ${q.customer_name}`, value: q.id })) }]),
          ...(v.so_id || v.quote_id ? [] : [{ key: "location_id", label: "Location", type: "select", required: true, options: fin.locations || [] }, { key: "customer_name", label: "Customer", required: true, max: 200 }]),
          { key: "vin", label: "VIN", required: true, max: 17 },
          { key: "make_name", label: "Make", required: true, max: 100 },
          { key: "model_name", label: "Model", required: true, max: 100 },
          { key: "model_year", label: "Year", type: "number", min: 1950 },
          { key: "colour", label: "Colour", max: 60 },
          { key: "odometer", label: "Odometer", type: "number", min: 0 },
          { key: "plate_number", label: "Plate", max: 24 },
          { key: "condition_grade", label: "Condition", type: "select", options: ["A", "B", "C", "D"].map((g) => ({ label: g, value: g })) },
          { key: "appraised_value", label: "Appraised value (actual cash value)", type: "number", step: "any", min: 0, required: true },
          { key: "offered_allowance", label: "Allowance offered to the customer", type: "number", step: "any", min: 0, required: true },
          { key: "lien_holder", label: "Finance company holding a lien", max: 150 },
          { key: "lien_payoff", label: "Lien payoff", type: "number", step: "any", min: 0 },
          { key: "remarks", label: "Remarks", type: "textarea", max: 1000 },
        ]}
      />
    </div>
  );
}

/* ------------------------------ returns ------------------------------ */

function ReturnView({ id, onHide, onChanged }) {
  const { data: r, loading, error, reload } = useLoad(() => dmsService.getReturn(id), [id]);
  const refresh = () => {
    reload();
    onChanged();
  };
  const doIt = (a) => run(() => dmsService.returnAction(id, a), refresh);
  return (
    <Panel title={r ? `Return ${r.return_number}` : "Return"} onHide={onHide} width={820}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {r ? (
          <>
            <DocHead title="" number={r.customer_name} status={r.status} sub={`Order ${r.so_number} · ${formatDate(r.return_date)} · ${r.reason_code}`}>
              {r.status === "DRAFT" ? <PrimaryButton onClick={() => doIt("submit")}>Submit</PrimaryButton> : null}
              {r.status === "APPROVED" ? <PrimaryButton onClick={() => doIt("receive")}>Receive vehicles</PrimaryButton> : null}
              {r.status === "RECEIVED" ? <PrimaryButton onClick={() => doIt("credit")}>Create credit memo</PrimaryButton> : null}
              {["DRAFT", "APPROVED"].includes(r.status) ? <SecondaryButton onClick={() => window.confirm("Cancel this return?") && doIt("cancel")}>Cancel</SecondaryButton> : null}
            </DocHead>
            <Section title="Vehicles">
              <DataTable
                rows={r.lines}
                rowKey={(l) => l.vin}
                columns={[
                  { label: "#", render: (l) => l.line_no },
                  { label: "Vehicle", render: (l) => l.description },
                  { label: "VIN", render: (l) => l.vin },
                  { label: "Credit (before VAT)", num: true, render: (l) => fmtMoney(l.credit_amount) },
                ]}
              />
              <div style={{ ...hint, marginTop: 8 }}>
                Restocking fee {fmtMoney(r.restocking_fee)} · credit {r.currency} {fmtMoney(r.credit_amount)} before VAT{r.credit_memo ? ` · credit memo ${r.credit_memo.trx_number} (${fmtMoney(r.credit_memo.total)})` : ""}
              </div>
            </Section>
            <Section title="Action history">
              <ActionHistory actions={r.actions} />
            </Section>
          </>
        ) : null}
      </LoadState>
    </Panel>
  );
}

// The delivered vehicles of an order, to pick the ones coming back.
function ReturnLines({ soId, chosen, onChange }) {
  const { data, loading, error, reload } = useLoad(() => dmsService.getOrder(soId), [soId]);
  const lines = data ? data.lines.filter((l) => ["DELIVERED", "INVOICED"].includes(l.status)) : [];
  return (
    <LoadState loading={loading} error={error} onRetry={reload} rows={2}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Vehicles coming back</div>
      {lines.length === 0 ? <div style={hint}>No delivered vehicles on this order.</div> : null}
      {lines.map((l) => (
        <label key={l.id} className="d-flex gap-2" style={{ fontSize: 14 }}>
          <input type="checkbox" checked={chosen.includes(l.id)} onChange={(e) => onChange(e.target.checked ? [...chosen, l.id] : chosen.filter((x) => x !== l.id))} />
          {l.vin} · {l.description} · {fmtMoney(l.net_amount)}
        </label>
      ))}
    </LoadState>
  );
}

export function ReturnOrders() {
  const reasons = useLookup("CANCEL_REASON");
  const orders = useLoad(() => dmsService.listOrders({ size: 200 }), []);
  const { data, loading, error, reload } = useLoad(() => dmsService.listReturns({}), []);
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);
  return (
    <div style={pageStyle}>
      <PageHeader title="Return Orders" subtitle="Vehicles brought back: back into stock and credited" actions={<PrimaryButton onClick={() => setCreating(true)}>New return</PrimaryButton>} />
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => setOpen(r.id)}
          empty={<EmptyState title="No returns" />}
          columns={[
            { label: "Number", render: (r) => <b>{r.return_number}</b> },
            { label: "Date", render: (r) => formatDate(r.return_date) },
            { label: "Order", render: (r) => r.so_number },
            { label: "Customer", render: (r) => r.customer_name },
            { label: "Credit", num: true, render: (r) => `${fmtMoney(r.credit_amount)} ${r.currency}` },
            { label: "Status", render: (r) => <DmsBadge status={r.status} /> },
          ]}
        />
      </LoadState>
      {open ? <ReturnView id={open} onHide={() => setOpen(null)} onChanged={reload} /> : null}
      <FormDrawer
        show={creating}
        title="New return order"
        initial={{ return_date: todayYmd(), so_line_ids: [] }}
        onHide={() => setCreating(false)}
        onDone={(d) => {
          reload();
          if (d && d.id) setOpen(d.id);
        }}
        width={620}
        onSave={(clean) => dmsService.createReturn({ ...clean, restocking_fee: Number(clean.restocking_fee || 0) })}
        fields={[
          { key: "so_id", label: "Sales order", type: "select", required: true, options: (orders.data || []).filter((o) => ["DELIVERED", "INVOICED", "CLOSED", "PARTIALLY_DELIVERED"].includes(o.status)).map((o) => ({ label: `${o.so_number} · ${o.customer_name}`, value: o.id })) },
          { key: "reason_code", label: "Reason", type: "select", required: true, options: reasons },
          { key: "return_date", label: "Return date", type: "date", required: true },
          { key: "restocking_fee", label: "Restocking fee", type: "number", step: "any", min: 0 },
          { key: "remarks", label: "Remarks", type: "textarea", max: 1000 },
        ]}
      >
        {(values, setValues) => (values.so_id ? <ReturnLines soId={values.so_id} chosen={values.so_line_ids || []} onChange={(ids) => setValues((s) => ({ ...s, so_line_ids: ids }))} /> : null)}
      </FormDrawer>
    </div>
  );
}

